// Render del cuerpo de una nota ENRIQUECIDA (`bodyHtml`): HTML sanitizado +
// resolución de las imágenes privadas. Es el hermano rico del NoteBody de
// TicketDetail. Réplica del RichNoteBody del CRM
// (crm/apps/web/src/shared/notes/RichNoteBody.tsx).
//
// Las imágenes llegan como `<img src="attachment:filename">`; tras pintar el
// HTML, cada una se casa por filename con un adjunto persistido y su src se
// sustituye por un objectURL autenticado (fetch con cookie — bucket privado),
// igual que InlineNoteImage. Un `attachment:` sin adjunto que case cae a su
// token de texto (fallback inocuo — p.ej. nota de ticket vista desde el
// timeline del lead, donde los adjuntos no se hidratan).

import { useEffect, useMemo, useRef } from "react";
import {
  ATTACHMENT_TOKEN_SCHEME,
  inlineImageToken,
} from "../../api/crmInlineImages";
import { TicketAttachment } from "../../api/crmTypes";
import { sanitizeNoteHtml } from "../../api/sanitizeEmailHtml";

export default function RichNoteBody({
  html,
  attachments,
  fetchObjectUrl,
  style,
}: {
  html: string;
  attachments: TicketAttachment[];
  /**
   * Descarga autenticada del blob de un adjunto → objectURL. DEBE ser estable
   * (useCallback en el caller); si cambia por render, bucle de descargas.
   * Omitido (timeline del lead): las imágenes caen al fallback de token.
   */
  fetchObjectUrl?: (attachmentId: string) => Promise<string>;
  style?: React.CSSProperties;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Sanitización SIEMPRE al render — nunca se confía en el HTML almacenado
  // (mismo criterio que el CRM y que el email del timeline).
  const clean = useMemo(() => sanitizeNoteHtml(html), [html]);

  // Caché attachmentId → objectURL VIVA entre re-ejecuciones del efecto: un
  // reload del listado (fijar una nota, publicar otra) trae arrays de adjuntos
  // nuevos en identidad pero idénticos en contenido; sin la caché, cada toggle
  // revocaría y re-descargaría todas las imágenes de la nota (parpadeo + N
  // streams redundantes). Se revoca todo solo al desmontar.
  const urlCache = useRef(new Map<string, string>());
  useEffect(() => {
    const cache = urlCache.current;
    return () => {
      cache.forEach((u) => URL.revokeObjectURL(u));
      cache.clear();
    };
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    let cancelled = false;

    for (const img of Array.from(root.querySelectorAll("img"))) {
      // El filename se conserva en un data-attr: si el efecto se re-ejecuta
      // (React NO re-crea el innerHTML mientras `clean` no cambie), el src ya
      // es un blob: y solo el data-attr permite volver a resolverlo.
      const src = img.getAttribute("src") ?? "";
      const filename = src.startsWith(ATTACHMENT_TOKEN_SCHEME)
        ? src.slice(ATTACHMENT_TOKEN_SCHEME.length)
        : img.dataset.crmAttachment;
      if (!filename) continue;
      img.dataset.crmAttachment = filename;

      const att = fetchObjectUrl
        ? attachments.find(
            (a) => a.filename === filename && a.contentType.startsWith("image/"),
          )
        : undefined;
      const prev = img.previousElementSibling as HTMLElement | null;
      const hasFallback = prev?.dataset.crmAttFallback === filename;
      if (!att) {
        // Fallback REVERSIBLE (ocultar + span hermano, no replaceWith): si los
        // adjuntos llegan en un render posterior con el MISMO html, React no
        // re-crea el innerHTML — el <img> debe seguir en el DOM para hidratarse.
        img.style.display = "none";
        if (!hasFallback) {
          const fallback = document.createElement("span");
          fallback.dataset.crmAttFallback = filename;
          fallback.textContent = inlineImageToken(
            filename,
            img.getAttribute("alt") || "imagen",
          );
          img.before(fallback);
        }
        continue;
      }
      if (hasFallback) prev?.remove();
      img.style.display = "";

      img.alt = img.alt || att.filename;
      img.loading = "lazy";
      img.style.maxWidth = "100%";
      if (!img.style.width) img.style.maxHeight = "320px";
      img.style.border = "1px solid #dee2e6";
      img.style.borderRadius = "0.4rem";
      img.style.cursor = "zoom-in";
      img.title = "Abrir a tamaño completo";
      img.onclick = () => {
        if (img.src) window.open(img.src, "_blank", "noopener");
      };
      const cached = urlCache.current.get(att.id);
      if (cached) {
        if (img.src !== cached) img.src = cached;
        continue;
      }
      fetchObjectUrl!(att.id)
        .then((url) => {
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          urlCache.current.set(att.id, url);
          img.src = url;
        })
        .catch(() => {
          img.alt = `[No se pudo cargar la imagen: ${filename}]`;
        });
    }

    return () => {
      cancelled = true;
    };
  }, [clean, attachments, fetchObjectUrl]);

  return (
    <div
      ref={containerRef}
      className="crm-rich-note"
      style={{
        fontSize: ".85rem",
        marginTop: "0.2rem",
        color: "#1f2933",
        ...style,
      }}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
